import React, { useEffect, useContext, useRef } from 'react'
import cn from 'classnames'
import PropTypes from 'prop-types'
import { IconSearchDefault, IconCloseDefault, Input } from '@mc/design-system'
import { useTranslation, withI18nContext } from '@mc/i18n'
import { track } from '@mc/track-event'
import { SearchContext } from '../SearchContext'
import i18nInstance from '../i18n/i18nInstance'
import classes from './SearchBar.module.scss'

// using forwardRef so it works in DropdownToggle
const SearchBar = React.forwardRef(
  (
    {
      appendIcon = (
        <IconCloseDefault className={cn(classes.close, 'mc-clickable')} />
      ),
      inputClassName,
      isFocused,
      hideCloseButton = false,
      prependIcon = <IconSearchDefault />,
      useLightTheme = false,
      wrapperClassName,
    },
    ref,
  ) => {
    const { setQuery, cancelSearch, category, query } =
      useContext(SearchContext)
    const inputRef = useRef(null)
    const { t } = useTranslation('@mc/algolia-search')

    useEffect(() => {
      if (isFocused && inputRef.current) {
        inputRef.current.focus()
      }
    }, [isFocused])

    const handleChange = (e) => {
      setQuery(e.target.value)
    }

    const hasQuery = query.length > 0
    // Categories only renders in logged out view
    const hasCategory = !!category
    const showCloseIcon = (hasQuery || hasCategory) && !hideCloseButton

    return (
      <>
        <div ref={ref} className={cn(wrapperClassName)}>
          {hasCategory ? (
            <div className='d-flex justify-content-between align-items-center mc-w-100'>
              <p className='mc-opacity--hinted'>{category}</p>
              <IconCloseDefault
                onClick={cancelSearch}
                data-testid='clear search'
                srText={t('a11y.clear')}
                size='sm'
                className='mc-opacity--muted mc-clickable'
              />
            </div>
          ) : (
            <div className='d-flex align-items-center mc-w-100'>
              <label htmlFor='search-bar' className='mc-sr-only'>
                {t('searchBar.searchInputLabel')}
              </label>
              <Input
                id='search-bar'
                ref={inputRef}
                placeholder={t('searchBar.searchInputLabel')}
                append={
                  showCloseIcon &&
                  React.cloneElement(appendIcon, {
                    'data-testid': 'clear search',
                    onClick: cancelSearch,
                  })
                }
                prepend={prependIcon}
                className={cn('mc-text-medium', classes.input, inputClassName, {
                  'mc-theme-light': useLightTheme,
                })}
                type='text'
                spellCheck='false'
                autoCorrect='false'
                onChange={handleChange}
                onFocus={() => track('Search Start')}
                value={query}
              />
            </div>
          )}
        </div>
      </>
    )
  },
)

SearchBar.propTypes = {
  appendIcon: PropTypes.element,
  currentRefinement: PropTypes.string,
  hideCloseButton: PropTypes.bool,
  inputClassName: PropTypes.string,
  isFocused: PropTypes.bool,
  isOpen: PropTypes.bool,
  prependIcon: PropTypes.element,
  refine: PropTypes.func,
  useLightTheme: PropTypes.bool,
  wrapperClassName: PropTypes.string,
}

SearchBar.displayName = 'SearchBar'
export default withI18nContext(i18nInstance)(SearchBar)
